import React, { useState } from "react";
import { Loader, Sparkles, Trash2, Check, ChevronDown, ChevronRight, ExternalLink, Plus, Minus, Pencil } from "lucide-react";

// Die Erzeugung der Abschnitte in drei Spalten: worum es geht, woraus es
// entsteht, was daraus wurde. Nebeneinander statt nacheinander, damit im
// Nachhinein nachvollziehbar bleibt, wie ein Satz zustande kam.

const QUELL_LABEL = {
  briefing: "Karriereportal-Formular",
  jotform: "Jotform-Formular",
  website: "Website",
  social: "Social Media",
  text: "Notizen",
};

// Aeltere Portale haben Stichpunkte als reine Zeichenketten gespeichert.
function alsPunkt(p) {
  if (typeof p === "string") return { text: p, herkunft: "" };
  return { text: (p && p.text) || "", herkunft: (p && p.herkunft) || "" };
}

export function QuellenLeiste({ quellen, briefing }) {
  if (!quellen.length && !briefing) return null;
  return (
    <div className="quellen-leiste">
      <div className="quellen-leiste-kopf">
        <strong>Woraus die Texte entstehen</strong>
        <span className="quellen-leiste-zahl">
          {quellen.length + (briefing ? 1 : 0)} Quelle{quellen.length + (briefing ? 1 : 0) === 1 ? "" : "n"}
        </span>
      </div>
      <div className="quellen-leiste-liste">
        {briefing && (
          <span className="quell-chip quell-chip--briefing">
            {QUELL_LABEL.briefing}
          </span>
        )}
        {quellen.map((q, i) => {
          const istLink = q.typ === "website" || q.typ === "social";
          const beschriftung = QUELL_LABEL[q.typ] || q.typ;
          return istLink ? (
            <a className="quell-chip" key={i} href={q.wert} target="_blank" rel="noreferrer noopener">
              {beschriftung}: {q.wert.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              <ExternalLink size={11} />
            </a>
          ) : (
            <span className="quell-chip" key={i} title={q.wert}>
              {beschriftung}
              {q.titel ? `: ${q.titel}` : ""}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function Spalte1({ block }) {
  const [regelnOffen, setRegelnOffen] = useState(false);
  const [formularOffen, setFormularOffen] = useState(false);
  const hatRegeln = !!(block.regeln || (block.beispiele || []).length);
  const antworten = block.formularAntworten || [];
  return (
    <div className="bl-spalte bl-spalte--was">
      <div className="bl-block-name">
        {block.freigegeben && <Check size={13} />}
        {block.name}
        {!block.pflicht && <span className="email-optional"> (optional)</span>}
      </div>
      {block.zuletzt && (
        <div className="bl-zuletzt-hinweis">
          Diesen Abschnitt zuletzt schreiben – erst wenn alle anderen Texte stehen,
          passt die Überschrift zum Rest.
        </div>
      )}
      <p className="bl-erklaerung">{block.erklaerung}</p>
      {block.aufbau && <p className="bl-aufbau">Aufbau: {block.aufbau}</p>}

      {antworten.length > 0 && (
        <>
          <button className="bl-regeln-toggle bl-formular-toggle"
            onClick={() => setFormularOffen(!formularOffen)}>
            {formularOffen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Antworten aus dem Formular ({antworten.length})
          </button>
          {formularOffen && (
            <div className="bl-formular-antworten">
              {antworten.map((a, i) => (
                <div className="bl-formular-antwort" key={i}>
                  <div className="bl-formular-feld">{a.feld}</div>
                  <div className="bl-formular-wert">{a.wert}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {hatRegeln && (
        <>
          <button className="bl-regeln-toggle" onClick={() => setRegelnOffen(!regelnOffen)}>
            {regelnOffen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Worauf hier zu achten ist
          </button>
          {regelnOffen && (
            <div className="bl-regeln">
              {block.regeln && <pre className="bl-regeln-text">{block.regeln}</pre>}
              {(block.beispiele || []).map((b, i) => (
                <div className="bl-beispiel" key={i}>
                  {b.schlecht && <div className="gate-bsp-schlecht">{b.schlecht}</div>}
                  {b.gut && <div className="gate-bsp-gut">{b.gut}</div>}
                  {b.warum && <div className="gate-bsp-warum">{b.warum}</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Spalte2({ block, busy, punkte, setPunkte, onVorbereiten, onSpeichern }) {
  if (block.automatisch) {
    return (
      <div className="bl-spalte bl-spalte--punkte">
        <p className="karriere-feld-hinweis">
          Wird beim Veröffentlichen aus den Stellenanzeigen des Partners gefüllt.
        </p>
      </div>
    );
  }

  if (!punkte.length) {
    return (
      <div className="bl-spalte bl-spalte--punkte">
        <div className="bl-knopfreihe">
          <button className="btn-secondary-sm" onClick={onVorbereiten} disabled={busy === block.id}>
            {busy === block.id
              ? <><Loader size={13} className="spin" /> Sammle …</>
              : <><Sparkles size={13} /> Stichpunkte sammeln</>}
          </button>
          {/* Ohne KI anfangen: manchmal weiss man selbst schon, was hier
              stehen soll, und die Quellen geben es gar nicht her. */}
          <button className="btn-secondary-sm"
            onClick={() => setPunkte([{ text: "", herkunft: "von dir" }])}>
            <Plus size={13} /> Selbst schreiben
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bl-spalte bl-spalte--punkte">
      {block.ausFormular > 0 && (
        <div className="bl-formular-hinweis">
          {block.ausFormular === 1
            ? "1 Punkt kommt direkt aus dem Formular"
            : `${block.ausFormular} Punkte kommen direkt aus dem Formular`}
          {block.ausFormular === punkte.length ? " – ohne KI." : "."}
        </div>
      )}
      {punkte.map((p, i) => (
        <div className="bl-punkt" key={i}>
          <textarea
            className="ai-prompt-input email-textarea"
            rows={2}
            value={p.text}
            onChange={(e) => setPunkte(punkte.map((x, j) => (i === j ? { ...x, text: e.target.value } : x)))}
          />
          <div className="bl-punkt-fuss">
            {p.herkunft
              ? <span className={`bl-herkunft ${/formular/i.test(p.herkunft) ? "bl-herkunft--formular" : ""}`}
                  title={p.herkunft}>aus: {p.herkunft}</span>
              : <span className="bl-herkunft bl-herkunft--ohne">ohne Quellenangabe</span>}
            <button className="gate-mini-btn" title="Punkt entfernen"
              onClick={() => setPunkte(punkte.filter((_, j) => j !== i))}>
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      ))}

      <button className="bl-punkt-neu"
        onClick={() => setPunkte([...punkte, { text: "", herkunft: "von dir" }])}>
        + Punkt
      </button>

      {(block.fehlt || []).length > 0 && (
        <div className="block-fehlt">
          <strong>Dafür fehlen Angaben:</strong>
          <ul>{block.fehlt.map((f, i) => <li key={i}>{f}</li>)}</ul>
        </div>
      )}

      <div className="bl-knopfreihe">
        <button className="btn-secondary-sm" onClick={onVorbereiten} disabled={busy === block.id}>
          {busy === block.id ? "…" : "Neu sammeln"}
        </button>
        <button className="btn-secondary-sm" onClick={() => onSpeichern(punkte, false)}
          disabled={busy === block.id + "-save"}>
          Speichern
        </button>
      </div>
    </div>
  );
}

function Spalte3({ block, busy, punkte, onAusformulieren, onSpeichern, onTextSpeichern }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [entwurf, setEntwurf] = useState(block.text || "");

  // Nach dem Ausformulieren steht ein neuer Text da - der Entwurf muss ihm
  // folgen, sonst bearbeitet man beim naechsten Klick den alten.
  React.useEffect(() => { setEntwurf(block.text || ""); }, [block.text]);

  if (block.automatisch) {
    return <div className="bl-spalte bl-spalte--text"><span className="bl-leer">—</span></div>;
  }

  const offen = block.offen || [];

  const speichern = () => {
    onTextSpeichern(block.id, entwurf);
    setBearbeiten(false);
  };

  if (bearbeiten) {
    return (
      <div className="bl-spalte bl-spalte--text">
        <textarea className="ai-prompt-input email-textarea bl-text-edit" rows={10}
          value={entwurf} onChange={(e) => setEntwurf(e.target.value)}
          placeholder="Schreib den Abschnitt hier selbst." />
        <div className="bl-knopfreihe">
          <button className="btn-secondary-sm"
            onClick={() => { setEntwurf(block.text || ""); setBearbeiten(false); }}>
            Abbrechen
          </button>
          <button className="btn-secondary-sm" onClick={speichern}
            disabled={busy === block.id + "-save"}>
            {busy === block.id + "-save" ? "Speichere …" : "Text speichern"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bl-spalte bl-spalte--text">
      {block.text ? (
        <>
          <div className="bl-text-kopf">
            {block.selbstGeschrieben
              ? <span className="bl-selbst">von dir geschrieben</span>
              : offen.length === 0
                ? <span className="block-text-ok"><Check size={12} /> Prüfung sauber</span>
                : <span className="block-text-offen">{offen.length} Punkt(e) offen</span>}
            {!block.selbstGeschrieben && (block.verlauf || []).length > 1 && (
              <span className="bl-runden">{block.verlauf.length} Runden</span>
            )}
          </div>
          <div className="bl-text">{block.text}</div>

          {offen.length > 0 && !block.selbstGeschrieben && (
            <details className="bl-offen">
              <summary>Was die Prüfer noch beanstanden</summary>
              <ul>
                {offen.map((o, i) => (
                  <li key={i}><strong>{o.agent}:</strong> {o.problem}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      ) : (
        <span className="bl-leer">Noch nicht ausformuliert.</span>
      )}

      <div className="bl-knopfreihe">
        <button className="btn-secondary-sm" onClick={() => onAusformulieren(block.id)}
          disabled={!punkte.length || busy === block.id + "-text"}>
          {busy === block.id + "-text"
            ? <><Loader size={13} className="spin" /> Schreibe & prüfe …</>
            : <><Sparkles size={13} /> {block.text ? "Neu" : "Ausformulieren"}</>}
        </button>
        {/* Der Text darf auch ganz ohne KI entstehen oder nachtraeglich
            geaendert werden. */}
        <button className="btn-secondary-sm" onClick={() => setBearbeiten(true)}>
          <Pencil size={13} /> {block.text ? "Bearbeiten" : "Selbst schreiben"}
        </button>
        {block.text && (
          <button className={`btn-secondary-sm ${block.freigegeben ? "active" : ""}`}
            onClick={() => onSpeichern(punkte, !block.freigegeben)}
            disabled={busy === block.id + "-save"}>
            {block.freigegeben ? "Freigegeben" : "Freigeben"}
          </button>
        )}
      </div>
    </div>
  );
}

function BlockReihe({ block, busy, onVorbereiten, onSpeichern, onAusformulieren, onTextSpeichern }) {
  const [punkte, setPunkte] = useState((block.stichpunkte || []).map(alsPunkt));

  // Nach dem Neuladen die Punkte aus der Antwort uebernehmen.
  React.useEffect(() => {
    setPunkte((block.stichpunkte || []).map(alsPunkt));
  }, [block.stichpunkte]);

  return (
    <div className={`bl-reihe ${block.freigegeben ? "bl-reihe--fertig" : ""}`}>
      <Spalte1 block={block} />
      <Spalte2 block={block} busy={busy} punkte={punkte} setPunkte={setPunkte}
        onVorbereiten={() => onVorbereiten(block.id)}
        onSpeichern={(p, frei) => onSpeichern(block.id, p, frei)} />
      <Spalte3 block={block} busy={busy} punkte={punkte}
        onAusformulieren={onAusformulieren}
        onTextSpeichern={onTextSpeichern}
        onSpeichern={(p, frei) => onSpeichern(block.id, p, frei)} />
    </div>
  );
}

export default function KarriereBloecke({ bloecke, busy, onVorbereiten, onSpeichern, onAusformulieren, onTextSpeichern }) {
  // Optionale Abschnitte kommen in der Regel nicht vor. Sie liegen deshalb
  // hinter einem Plus, statt die Tabelle zu verlaengern.
  const [optionaleOffen, setOptionaleOffen] = useState(false);

  // Was gar nicht ins Portal soll (Bewerbungsprozess, offene Stellen), wird
  // hier auch nicht gezeigt.
  const sichtbar = bloecke.filter((b) => !b.versteckt);
  const fest = sichtbar.filter((b) => !b.eingeklappt);
  const optional = sichtbar.filter((b) => b.eingeklappt);

  const reihe = (b) => (
    <BlockReihe key={b.id} block={b} busy={busy}
      onVorbereiten={onVorbereiten} onSpeichern={onSpeichern} onAusformulieren={onAusformulieren}
      onTextSpeichern={onTextSpeichern} />
  );

  return (
    <div className="bl-tabelle">
      <div className="bl-kopfzeile">
        <div className="bl-spalte bl-spalte--was">Worum geht es?</div>
        <div className="bl-spalte bl-spalte--punkte">Stichpunkte &amp; Herkunft</div>
        <div className="bl-spalte bl-spalte--text">Text</div>
      </div>

      {fest.map(reihe)}

      {optional.length > 0 && (
        <div className="bl-optional-zeile">
          <button className="bl-optional-knopf" onClick={() => setOptionaleOffen(!optionaleOffen)}>
            {optionaleOffen ? <Minus size={13} /> : <Plus size={13} />}
            {optionaleOffen
              ? `${optional.length} optionale Abschnitte ausblenden`
              : `${optional.length} optionale Abschnitte einblenden`}
          </button>
          <span className="bl-optional-namen">{optional.map((b) => b.name).join(" · ")}</span>
        </div>
      )}

      {optionaleOffen && optional.map(reihe)}
    </div>
  );
}
